/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Nov 18 2024
 **/

import { useQuery } from "@tanstack/react-query";

import { Loader, Menu } from "@/frameworks/designStuff";
import { Maybe } from "@/frameworks/libraries";
import { usePersistActions } from "@/frameworks/core/persistence";
import { useChatRoomActions } from "@/frameworks/core/chat";
import { usePopover } from "@/frameworks/core/hooks";
import { GROUP_POPOVER_ID } from "@/frameworks/core/commons";

import * as S from "./emotion";

type OpenWorldChatOptionListProps = {
	groupID: string;
	spaceID: string;
};
/**
 * OpenWorld는 그룹에 귀속된 채팅방이므로 DMChatOptionList와는 다르게 그룹에 대한 설정을 제공해야 한다.
 */
export default function OpenWorldChatOptionList(props: OpenWorldChatOptionListProps) {
	const { spaces } = usePersistActions();
	const chatRoomActions = useChatRoomActions();
	const popover = usePopover();

	const { isLoading, data } = useQuery({
		queryKey: ["openworld", "chat", props.groupID],
		queryFn: async () => {
			const { groups, openWorlds } = await spaces.refresh_space_all();
			const group = groups.find((group) => group.id === props.groupID);
			if (group) {
				return group.groupOpenWorlds;
			} else {
				return openWorlds;
			}
		},
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		refetchOnMount: true,
		enabled: Boolean(props.groupID)
	});

	const handleClick = async (e: React.MouseEvent<HTMLLIElement>, spaceID: SPACE_ID) => {
		e.stopPropagation();
		const room = await chatRoomActions.spaceChat.openworld.join(spaceID, spaces.get_space(spaceID)?.name || "");
		if (!room?.roomId) throw new Error("roomId is not exist");
		await chatRoomActions.common.open({
			channelID: room.roomId,
			type: "Space",
			spaceID: spaceID || ""
		});
		popover.hide(GROUP_POPOVER_ID.CHAT_OPTION);
	};

	return (
		<S.SubMenu>
			<Menu.List>
				<Maybe test={isLoading}>
					<Menu.Item>
						<Loader />
					</Menu.Item>
					<Maybe.Fallback>
						{data?.map((space) => (
							<Menu.Item
								key={space.spaceId}
								onClick={(e) => handleClick(e, space.spaceId)}
								focus={space.spaceId === props.spaceID}
							>
								{(space as SwaggerModels.OpenWorldResponseVO).name ||
									(space as SwaggerModels.MySpaceResponseVO).spaceName ||
									""}
							</Menu.Item>
						))}
					</Maybe.Fallback>
				</Maybe>
			</Menu.List>
		</S.SubMenu>
	);
}
